import React, { useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { fetchProducts, processBatch } from '../../services/productFeed';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Select } from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { Loader2, Play, CheckCircle } from "lucide-react";

const columnHelper = createColumnHelper();

const FeedSelectionModal = ({ isOpen, onClose, store }) => {
    // Pagination state
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    // Multi-select state
    const [selectedProducts, setSelectedProducts] = useState(new Set());
    
    // Processing state
    const [isProcessing, setIsProcessing] = useState(false);
    const [processResult, setProcessResult] = useState(null);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['products', pagination.pageIndex + 1, pagination.pageSize],
        queryFn: () => fetchProducts(pagination.pageIndex + 1, pagination.pageSize),
        keepPreviousData: true,
    });

    const tableData = useMemo(() => data?.products || [], [data]);
    const totalCount = data?.total || 0;

    // Toggle product selection
    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    };

    // Toggle select all
    const toggleSelectAll = () => {
        if (selectedProducts.size === tableData.length && tableData.length > 0) {
            setSelectedProducts(new Set());
        } else {
            setSelectedProducts(new Set(tableData.map(p => p.id)));
        }
    };

    // Get selected products data
    const getSelectedProductsData = () => {
        return tableData.filter(p => selectedProducts.has(p.id));
    };

    const handleProcessBatch = async () => {
        if (selectedProducts.size === 0) return;

        setIsProcessing(true);
        setProcessResult(null);

        try {
            const productsToProcess = getSelectedProductsData();
            // Get current template JSON from store
            const templateData = store.toJSON();
            
            const result = await processBatch(productsToProcess, templateData);
            setProcessResult({
                success: true,
                message: result.message || `Successfully processed ${productsToProcess.length} products.`,
                details: result
            });
            
            // Clear selection after successful processing
            setSelectedProducts(new Set());
            
        } catch (err) {
            setProcessResult({
                success: false,
                message: err.message || "Failed to process batch."
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'select',
            header: () => (
                <input
                    type="checkbox"
                    checked={selectedProducts.size === tableData.length && tableData.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-[#8E0DFF] bg-gray-100 border-gray-300 rounded focus:ring-[#8E0DFF]"
                />
            ),
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selectedProducts.has(row.original.id)}
                    onChange={() => toggleProductSelection(row.original.id)}
                    className="w-4 h-4 text-[#8E0DFF] bg-gray-100 border-gray-300 rounded focus:ring-[#8E0DFF]"
                />
            ),
        }),
        columnHelper.accessor('images', {
            header: 'Image',
            cell: info => {
                const images = info.getValue();
                const firstImage = images && images.length > 0 ? images[0].src : null;
                return firstImage ? (
                    <img src={firstImage} alt="Product" className="h-10 w-10 object-cover rounded-md" />
                ) : (
                    <span className="text-gray-400 text-sm italic">No Image</span>
                );
            },
        }),
        columnHelper.accessor('title', {
            header: 'Title',
            cell: info => <span className="font-medium text-left line-clamp-2">{info.getValue()}</span>,
        }),
        columnHelper.accessor('min_price', {
            header: 'Price',
            cell: info => {
                const price = info.getValue();
                const currency = info.row.original.currency || 'INR';
                return <div className="text-left w-20">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(price)}</div>;
            },
        }),
    ], [selectedProducts, tableData.length, toggleSelectAll]);

    const table = useReactTable({
        data: tableData,
        columns,
        pageCount: Math.ceil(totalCount / pagination.pageSize),
        state: {
            pagination,
        },
        onPaginationChange: setPagination,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col h-[80vh]">
                <DialogHeader>
                    <DialogTitle>Select Feed for Batch Processing</DialogTitle>
                    <DialogDescription>
                        Select products to process with the current template.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-auto border rounded-md">
                    {isError && <div className="bg-destructive/15 text-destructive p-3 rounded-md mb-4">Error: {error.message}</div>}
                    
                    {processResult && (
                        <div className={`p-4 mb-4 rounded-md flex items-center gap-2 ${processResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                            {processResult.success ? <CheckCircle className="h-5 w-5" /> : <Loader2 className="h-5 w-5" />}
                            <span>{processResult.message}</span>
                        </div>
                    )}

                    <Table>
                        <TableHeader className="bg-muted/30 sticky top-0 z-10">
                            {table.getHeaderGroups().map(headerGroup => (
                                <TableRow key={headerGroup.id} className="hover:bg-muted/30 border-b border-border">
                                    {headerGroup.headers.map(header => (
                                        <TableHead key={header.id} className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50">
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        <div className="flex justify-center items-center gap-2 text-muted-foreground">
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Loading products...
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map(row => (
                                    <TableRow key={row.id} className="transition-colors hover:bg-muted/30 data-[state=selected]:bg-muted">
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell key={cell.id} className="p-3 align-middle text-left">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                                        No products found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <div className="flex items-center justify-between py-2 border-t mt-2">
                    <div className="flex-1 text-sm text-muted-foreground">
                        {selectedProducts.size} selected
                    </div>
                    
                    <div className="flex items-center space-x-2 mr-4">
                         <div className="text-sm text-muted-foreground mr-2">
                            Page {table.getState().pagination.pageIndex + 1} of {Math.max(1, table.getPageCount())}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Next
                        </Button>
                    </div>
                </div>

                <DialogFooter className="border-t pt-4">
                     <Button variant="outline" onClick={onClose} disabled={isProcessing}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleProcessBatch} 
                        disabled={selectedProducts.size === 0 || isProcessing}
                        className="bg-[#8E0DFF] hover:bg-[#7b0acc]"
                    >
                        {isProcessing ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Play className="mr-2 h-4 w-4" />
                                Process Batch
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default FeedSelectionModal;
